import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useClipboard } from '@vueuse/core';
import { ResourceType } from '../../types/resources';
import useMoveCard from './useMoveCard';
import useManageCardMembers from '../../boards/composables/useManageCardMembers';
import useTranslate from './useTranslate';
import dropCardIntoBoard from '../dropCardIntoBoard';
import { globalState } from '../globalState';
import getMemberLabel from '../getMemberLabel';
import filterMembers from '../filterMembers';
import { getCurrentDateInSeconds } from '../timeDifference';
import getDashedTitle from '../getDashedTitle';
import constants from '../constants';

const BULK_NOTIFICATION_ID = 'bulk-action-notification';

export default function useBulkActions() {
  const store = useStore();
  const { translate } = useTranslate();
  const { copy } = useClipboard();

  const abortController = ref<AbortController | null>(null);
  const completedActions = ref<number>(0);
  const bulkMembersSearchValue = ref<string>('');
  const bulkTagsSearchValue = ref<string>('');

  const { moveCardToDifferentList } = useMoveCard();
  const { assignMember, unassignMember } = useManageCardMembers();

  let currentNotification: any = null;
  let removeNotificationTimeout: NodeJS.Timeout | null = null;

  // computeds:

  const multiSelectCards = computed(() => {
    return store.getters.multiSelectedCardIds;
  });

  /**
   * Creates a computed property that returns a common date value from all selected cards
   * If all cards have the same value for the specified property, returns that value
   * Otherwise, returns the current date as fallback
   */
  const createBulkDateComputed = (dateProperty: 'due_date' | 'start_date') => {
    return computed(() => {
      const dates = multiSelectCards.value.map((cardId: string) => {
        return store.getters.getCards[cardId]?.[dateProperty];
      });

      if (dates.every((date: number | null) => date === dates[0])) {
        return dates[0];
      }

      return getCurrentDateInSeconds();
    });
  };

  const bulkSelectedDueDate = createBulkDateComputed('due_date');
  const bulkSelectedStartDate = createBulkDateComputed('start_date');

  const cards = computed(() => store.getters.getCards);

  const tags = computed(() => store.getters.getTags);

  const projectTags = computed(() =>
    store.getters.getProjectTags(store.getters.getCurrentProjectId)
  );
  const workspaceTags = computed(() => store.getters.getWorkspaceTags(store.getters.getTeamID));
  const projectWorkspaceTags = computed(() => {
    return [...projectTags.value, ...workspaceTags.value];
  });

  const bulkTags = computed(() => {
    return projectWorkspaceTags.value
      .map((tagId: string) => {
        const stateTag = tags.value[tagId];
        const checked = multiSelectCards.value.every((cardId: string) => {
          return cards.value[cardId]?.tags?.some((t: any) => t.id === stateTag?.id);
        });
        const partiallyChecked = multiSelectCards.value.some((cardId: string) => {
          return cards.value[cardId]?.tags?.some((t: any) => t.id === stateTag?.id);
        });
        return {
          ...stateTag,
          id: stateTag?.id,
          label: stateTag?.name,
          color: stateTag?.color,
          icon: 'tag',
          key: 'tags',
          customIcon: true,
          customIconColor: stateTag?.color,
          checkbox: true,
          checked,
          partiallyChecked,
          lighter: true,
          avatar: false,
          editable: false,
        };
      })
      .sort((a: any, b: any) => {
        if (a.checked && !b.checked) return -1;
        if (!a.checked && b.checked) return 1;
        if (a.partiallyChecked && !b.partiallyChecked) return -1;
        if (!a.partiallyChecked && b.partiallyChecked) return 1;
        return a.label.localeCompare(b.label);
      })
      .filter((tag: any) => {
        return tag.label.toLowerCase().includes(bulkTagsSearchValue.value.toLowerCase());
      });
  });

  const bulkMembers = computed(() => {
    // Force reactivity by accessing cards computed first
    const currentCards = cards.value;

    const members = Object.values(store.getters.getTeamMembers)
      .map((member: any) => {
        const checked = multiSelectCards.value.every((cardId: string) => {
          return currentCards[cardId]?.members?.some((m: any) => m.user_id === member.id);
        });
        const partiallyChecked = multiSelectCards.value.some((cardId: string) => {
          return currentCards[cardId]?.members?.some((m: any) => m.user_id === member.id);
        });
        return {
          ...member,
          avatar: true,
          id: member.id,
          lighter: true,
          checkbox: true,
          display_name: getMemberLabel(member),
          label: getMemberLabel(member),
          checked,
          partiallyChecked,
        };
      })
      .sort((a: any, b: any) => {
        // sort by checked, then partiallyChecked, then display_name
        if (a.checked && !b.checked) return -1;
        if (!a.checked && b.checked) return 1;
        if (a.partiallyChecked && !b.partiallyChecked) return -1;
        if (!a.partiallyChecked && b.partiallyChecked) return 1;
        return a.display_name.localeCompare(b.display_name);
      });

    return filterMembers(members, bulkMembersSearchValue.value);
  });

  const allCardsAlreadyFavourited = computed(() => {
    return multiSelectCards.value.every((cardId: string) => {
      return store.getters.isFavourite(cardId, ResourceType.Card);
    });
  });

  const allCardsSubscribed = computed(() => {
    return multiSelectCards.value.every((cardId: string) => {
      return store.getters.getCards[cardId]?.is_watching;
    });
  });

  // methods:
  const clearMultiSelectedCardIds = () => {
    store.dispatch('clearMultiSelectedCardIds');
  };

  const bulkChangeStatus = async (status: any) => {
    return bulkActionWrapper(async (cardId) => {
      return moveCardToDifferentList(status.list_id, cardId);
    }, translate('changingStatus'));
  };

  const bulkSetCardDates = async (fieldType: 'start_date' | 'due_date', date: Date) => {
    return bulkActionWrapper(async (cardId) => {
      return store.dispatch('updateCardWithPatchRequest', {
        cardId,
        card: { [fieldType]: date },
      });
    }, translate('changingDates'));
  };

  const bulkMemberChange = async (member: any) => {
    return bulkActionWrapper(async (cardId) => {
      return member.checked
        ? unassignMember(cardId, member, member.id, 0)
        : assignMember(cardId, member.id, 0);
    }, translate('changingAssignees'));
  };

  const bulkAddFavourite = async () => {
    return bulkActionWrapper(async (cardId) => {
      return store.dispatch('addFavourite', {
        resource_id: cardId,
        resource_type: ResourceType.Card,
        title: store.getters.getCards[cardId]?.title,
        noNotification: true,
      });
    }, translate('addingToFavourites'));
  };

  const bulkRemoveFavourite = async () => {
    return bulkActionWrapper(async (cardId) => {
      return store.dispatch('deleteUserFavourite', {
        resource_id: cardId,
        resource_type: ResourceType.Card,
        noNotification: true,
      });
    }, translate('removingFromFavourites'));
  };

  const bulkSubscribe = async (subscribe: boolean) => {
    return bulkActionWrapper(
      async (cardId) => {
        return store.dispatch('watchCard', {
          resourceId: cardId,
          subscribe,
          noNotification: true,
        });
      },
      translate(subscribe ? 'subscribingToResourceUpdates' : 'unsubscribingFromResourceUpdates')
    );
  };

  const bulkTagOperation = async (
    tag: any,
    localAction: string,
    apiAction: string,
    revertAction: string,
    getLocalPayload: (cardId: string) => any,
    getRevertPayload: (cardId: string) => any
  ) => {
    return bulkActionWrapper(async (cardId) => {
      store.dispatch(localAction, getLocalPayload(cardId));
      return store.dispatch(apiAction, { cardId, tagId: tag.id }).catch(() => {
        store.dispatch(revertAction, getRevertPayload(cardId));
      });
    }, translate('changingTags'));
  };

  const bulkAddTag = async (tag: any) => {
    return bulkTagOperation(
      tag,
      'localAddTagToCard',
      'addTagToCard',
      'localDeleteTagFromCard',
      (cardId) => ({ cardId, tag }),
      (cardId) => ({ tagId: tag.id, cardId })
    );
  };

  const bulkRemoveTag = async (tag: any) => {
    return bulkTagOperation(
      tag,
      'localDeleteTagFromCard',
      'removeCardTag',
      'localAddTagToCard',
      (cardId) => ({ cardId, tagId: tag.id }),
      (cardId) => ({ cardId, tag })
    );
  };

  const bulkTagChange = async (tag: any) => {
    return tag.checked ? bulkRemoveTag(tag) : bulkAddTag(tag);
  };

  const addUiNotification = (
    message: string,
    subtitle?: string,
    icon?: string,
    duration?: number
  ) => {
    currentNotification = {
      id: BULK_NOTIFICATION_ID,
      message,
      subtitle,
      icon,
      duration: duration ?? 4000,
    };
    store.dispatch('addUiNotification', currentNotification);
  };

  const removeUiNotification = () => {
    store.dispatch('removeUiNotificationById', BULK_NOTIFICATION_ID);
  };

  const updateUiNotificationProgress = (cardCount: number, updateMessage?: string) => {
    currentNotification.subtitle = getProgress(cardCount);
    if (updateMessage) {
      currentNotification.message = updateMessage;
    }
    removeUiNotification();
    store.dispatch('addUiNotification', currentNotification);
  };

  const getProgress = (cardCount: number) => {
    return `${completedActions.value} / ${cardCount} ${translate('processed')}`;
  };

  const onActionCompleted = () => {
    globalState.bulkActionsInProgress = false;
    removeNotificationTimeout = setTimeout(() => {
      removeUiNotification();
      if (removeNotificationTimeout) {
        clearTimeout(removeNotificationTimeout);
        removeNotificationTimeout = null;
      }
      currentNotification = null;
    }, 4000);
    abortController.value = null;
  };

  const onActionStarted = (notificationTitle: string) => {
    abortController.value?.abort();
    abortController.value = new AbortController();
    globalState.bulkActionsInProgress = true;
    const cardCount = multiSelectCards.value.length;
    completedActions.value = 0;
    if (removeNotificationTimeout) {
      clearTimeout(removeNotificationTimeout);
      removeNotificationTimeout = null;
    }
    addUiNotification(notificationTitle, `${getProgress(cardCount)}`, 'card-icon', -1);
  };

  const onCardProcessed = (cardCount: number) => {
    completedActions.value++;
    updateUiNotificationProgress(cardCount);
  };

  const bulkActionWrapper = async (
    action: (cardId: string) => Promise<any>,
    notificationTitle: string
  ) => {
    onActionStarted(notificationTitle);
    const cardCount = multiSelectCards.value.length;
    const promises: Promise<void>[] = [];
    for (const cardId of multiSelectCards.value) {
      if (abortController.value?.signal.aborted) {
        updateUiNotificationProgress(cardCount, translate('cancelled'));
        break;
      }
      const promise = await action(cardId);
      promises.push(promise);
      onCardProcessed(cardCount);
    }
    Promise.all(promises).then(onActionCompleted);
  };

  const bulkMoveCardToBoard = async (boardId: string) => {
    return bulkActionWrapper(async (cardId) => {
      return dropCardIntoBoard({
        cardId,
        destinationBoardId: boardId,
        showNotification: false,
      });
    }, translate('moveCardIntoBoard'));
  };

  const copyCardTitlesToClipboard = async () => {
    const cardCount = multiSelectCards.value.length;
    const cardTitles = multiSelectCards.value
      .map((cardId: string) => {
        const card = store.getters.getCards[cardId];
        return card?.title || '';
      })
      .filter((title: string) => title.trim() !== '')
      .map((title: string) => `- ${title}`)
      .join('\n');

    if (cardTitles) {
      await copy(cardTitles);
      addCopyNotification(cardCount);
    }
  };

  const addCopyNotification = (cardCount: number) => {
    store.dispatch('addUiNotification', {
      message: translate('copiedToClipboard'),
      subtitle: `${cardCount} ${translate('cards')}`,
      icon: 'copy',
      duration: 3000,
    });
  };

  const copyCardTitlesAsLinksToClipboard = async () => {
    const teamSlug = store.getters.getTeamSlug;
    const cardCount = multiSelectCards.value.length;
    const cardLinks = multiSelectCards.value
      .map((cardId: string) => {
        const card = store.getters.getCards[cardId];
        if (!card?.title || card.title.trim() === '') {
          return '';
        }

        const dashedTitle = getDashedTitle(card.title);
        let pathName = ResourceType.Card as string;

        if (card.type === ResourceType.Epic) {
          pathName = constants.epicRoute.path;
        } else if (card.sprint_id) {
          pathName = ResourceType.SprintCard as string;
        }

        const cardUrl = `${window.location.origin}/${teamSlug}/${pathName}-${card.id}-${dashedTitle}`;
        return `<li><a href="${cardUrl}">${card.title}</a></li>`;
      })
      .filter((link: string) => link.trim() !== '');

    if (cardLinks.length > 0) {
      const htmlContent = `<ul>${cardLinks.join('')}</ul>`;
      const plainTextContent = cardLinks
        .map((item: string) => {
          const match = item.match(/<li><a href="[^"]*">([^<]*)<\/a><\/li>/);
          return match ? `- ${match[1]}` : '';
        })
        .filter((item: string) => item)
        .join('\n');

      try {
        await navigator.clipboard.write([
          new ClipboardItem({
            'text/html': new Blob([htmlContent], { type: 'text/html' }),
            'text/plain': new Blob([plainTextContent], { type: 'text/plain' }),
          }),
        ]);
      } catch (error) {
        // Fallback to plain text if HTML clipboard isn't supported
        await copy(plainTextContent);
      }

      addCopyNotification(cardCount);
    }
  };

  const stopBulkAction = () => {
    abortController.value?.abort();
  };

  const bulkActionInProgress = computed(() => {
    return globalState.bulkActionsInProgress;
  });

  const selectAllBoardsCards = (boardId: string) => {
    const beardsCards = store.getters.getBoardsCardsIdsByBoardId(boardId);
    store.dispatch('setMultiSelectedCardIds', beardsCards);
  };

  const escMultiSelectHandler = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && multiSelectCards.value.length > 0) {
      // if we are in bulk actions, we need to stop the bulk action
      if (bulkActionInProgress.value) {
        stopBulkAction();
        return true;
      }
      clearMultiSelectedCardIds();
      return true;
    }
    return false;
  };

  return {
    allCardsAlreadyFavourited,
    allCardsSubscribed,
    bulkMembers,
    bulkSelectedDueDate,
    bulkSelectedStartDate,
    completedActions,
    clearMultiSelectedCardIds,
    bulkChangeStatus,
    bulkSetCardDates,
    multiSelectCards,
    bulkMemberChange,
    bulkAddFavourite,
    bulkRemoveFavourite,
    bulkMoveCardToBoard,
    bulkSubscribe,
    bulkTags,
    bulkTagChange,
    bulkMembersSearchValue,
    bulkTagsSearchValue,
    copyCardTitlesToClipboard,
    copyCardTitlesAsLinksToClipboard,
    stopBulkAction,
    bulkActionInProgress,
    selectAllBoardsCards,
    escMultiSelectHandler,
  };
}
